// src/components/BecomeTasker.js
import { useState, useEffect } from "react";
import { Download, ShieldCheck, Wallet, Zap } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";

const STORE = {
  ios: "https://apps.apple.com/sa/app/trustfix-partners/id6797325119",
  android: "https://play.google.com/store/apps/details?id=com.trustfix.partnerapp",
};

const CONTENT = {
  ar: {
    docTitle: "انضم كمقدّم خدمة",
    subtitle: "TrustFix — ترستفيكس",
    intro:
      "نمِّ عملك مع ترستفيكس. احصل على مهام قريبة منك، وحدّد أسعارك، واحصل على أجرك مباشرةً — كل ذلك من تطبيق واحد. حمّل تطبيق مقدّمي الخدمة للبدء.",
    perks: [
      ["مطابقة فورية للمهام", "تصلك المهام القريبة في الوقت الفعلي، واقبل ما تريد منها."],
      ["تحتفظ بما تكسبه", "تتّفق على السعر مباشرةً مع العميل. تتقاضى ترستفيكس رسم منصّة ثابتاً لكل مهمة فقط."],
      ["موثوق ومعتمد", "انضم إلى شبكة مقدّمي خدمة مُدقّقين، وابنِ سمعتك مع كل مهمة مكتملة."],
    ],
    downloadTitle: "حمّل تطبيق مقدّمي الخدمة",
    downloadDesc: "متاح على iOS و Android.",
    iosCta: "App Store",
    androidCta: "Google Play",
    scanNote:
      "امسح رمز QR بهاتفك، أو اضغط على أحد الأزرار لفتح المتجر.",
    iosAlt: "امسح للتحميل على iPhone",
    androidAlt: "امسح للتحميل على Android",
  },
  en: {
    docTitle: "Become a Tasker",
    subtitle: "TrustFix — ترستفيكس",
    intro:
      "Grow your business with TrustFix. Get matched with nearby jobs, set your own prices, and get paid directly — all from one app. Download the provider app to get started.",
    perks: [
      ["Instant job matching", "Get nearby jobs dispatched to you in real time and accept the ones you want."],
      ["You keep what you earn", "Agree the price directly with the customer. TrustFix only charges a fixed platform fee per job."],
      ["Verified & trusted", "Join a vetted network of providers and build your reputation with every completed job."],
    ],
    downloadTitle: "Download the Provider App",
    downloadDesc: "Available on iOS and Android.",
    iosCta: "App Store",
    androidCta: "Google Play",
    scanNote:
      "Scan a QR code with your phone, or tap a button to open the store.",
    iosAlt: "Scan to download for iPhone",
    androidAlt: "Scan to download for Android",
  },
};

const PERK_ICONS = [<Zap size={28} />, <Wallet size={28} />, <ShieldCheck size={28} />];

export default function BecomeTasker() {
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem("tf_lang") || "ar";
    } catch {
      return "ar";
    }
  });

  const isAr = lang === "ar";
  const t = CONTENT[lang];

  useEffect(() => {
    try {
      localStorage.setItem("tf_lang", lang);
    } catch {}
  }, [lang]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header lang={lang} setLang={setLang} />
      <div className="min-h-screen" dir={isAr ? "rtl" : "ltr"}>
        {/* Title */}
        <section>
          <div className="max-w-4xl mx-auto px-5 pt-3 pb-3">
            <h1 className="text-[26px] font-extrabold text-[#006C35] mt-2 mb-1">
              {t.docTitle}
            </h1>
            <p className="text-gray-500 text-[15px] mt-0 mb-4">{t.subtitle}</p>
            <p className="text-gray-600 leading-relaxed max-w-2xl">{t.intro}</p>
          </div>
        </section>

        {/* Perks + download */}
        <section className="bg-white">
          <div className="max-w-4xl mx-auto px-5 pt-8 pb-12">
            {/* Perks */}
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {t.perks.map(([title, desc], i) => (
                <div
                  key={title}
                  className="bg-gray-50 rounded-xl p-6 border border-gray-100"
                >
                  <div className="w-12 h-12 bg-[#EDEEF2] rounded-lg flex items-center justify-center text-[#535C79] mb-4">
                    {PERK_ICONS[i]}
                  </div>
                  <h3 className="text-[#1E3A8A] font-bold mb-2">{title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>

            {/* Download card */}
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-8">
              <h2 className="text-lg font-bold text-[#006C35] mb-2">
                {t.downloadTitle}
              </h2>
              <p className="text-gray-600 mb-6">{t.downloadDesc}</p>

              <div className="grid sm:grid-cols-2 gap-6">
                {/* iOS */}
                <div className="border border-gray-200 rounded-xl p-5 flex flex-col items-center text-center">
                  <div className="bg-white border border-gray-200 rounded-xl p-3 mb-4">
                    <img
                      src="/tasker-ios-qr.jpeg"
                      alt={t.iosAlt}
                      className="w-32 h-32 object-contain"
                    />
                  </div>
                  <a
                    href={STORE.ios}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#006C35] text-white px-5 py-2.5 rounded-lg hover:bg-[#005428] transition-colors w-full justify-center"
                  >
                    <Download size={18} />
                    {t.iosCta}
                  </a>
                </div>

                {/* Android */}
                <div className="border border-gray-200 rounded-xl p-5 flex flex-col items-center text-center">
                  <div className="bg-white border border-gray-200 rounded-xl p-3 mb-4">
                    <img
                      src="/tasker-android-qr.jpeg"
                      alt={t.androidAlt}
                      className="w-32 h-32 object-contain"
                    />
                  </div>
                  <a
                    href={STORE.android}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#006C35] text-white px-5 py-2.5 rounded-lg hover:bg-[#005428] transition-colors w-full justify-center"
                  >
                    <Download size={18} />
                    {t.androidCta}
                  </a>
                </div>
              </div>

              <p className="text-sm text-gray-500 mt-5">{t.scanNote}</p>
            </div>
          </div>
        </section>
      </div>
      <Footer lang={lang} />
    </div>
  );
}